import { ApiError, identity, unwrap } from "../api";
import type { IdentityComponents } from "@steelyard/api-client";

/**
 * The text pages customers read — privacy policy, terms, … (`../Steelyard` contracts/content-pages.md,
 * `ContentPagesController.cs`). Saving and deleting are `config.administer`; the list and the read
 * are anonymous — the same calls the portal and the public website make. The API sanitises the HTML.
 */
export type ContentPage = IdentityComponents["schemas"]["ContentPageView"];
export type ContentPageSummary = IdentityComponents["schemas"]["ContentPageSummary"];

/** The address the portal's footer and the website's privacy.html are built around. */
export const PRIVACY_POLICY_SLUG = "privacy-policy";

/** By title. */
export async function listContentPages() {
  return unwrap(identity().GET("/api/identity/pages"));
}

/** Null when no page has that address (the API's 404). */
export async function getContentPage(slug: string): Promise<ContentPage | null> {
  try {
    return await unwrap(identity().GET("/api/identity/pages/{slug}", { params: { path: { slug } } }));
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) return null;
    throw err;
  }
}

/** Creates the page at `slug` or replaces it; answers it as stored (sanitised). */
export async function saveContentPage(slug: string, input: { title: string; body: string }) {
  return unwrap(identity().PUT("/api/identity/pages/{slug}", { params: { path: { slug } }, body: input }));
}

export async function deleteContentPage(slug: string) {
  return unwrap(identity().DELETE("/api/identity/pages/{slug}", { params: { path: { slug } } }));
}

/** A title as an address: "Terms & Conditions" → "terms-conditions". The API holds the same rule. */
export function slugify(title: string): string {
  return title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60)
    .replace(/-+$/, "");
}

/** Where customers read a page on each site. The privacy policy keeps its own addresses. */
export function publicAddresses(slug: string) {
  return slug === PRIVACY_POLICY_SLUG
    ? { portal: "/privacy-policy", website: "privacy.html" }
    : { portal: `/page/${slug}`, website: `page.html?p=${slug}` };
}
